import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "parts/Header";
import Footer from "parts/Footer";
import { Fade } from "react-awesome-reveal";
import Discuss from "parts/Discuss";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Target,
  Users,
  TrendingUp,
  Globe,
  Sparkles,
  ArrowRight,
  Play,
  Building,
  Code,
  Shield,
  DollarSign,
  BarChart3,
  Briefcase,
  CheckCircle,
  Star,
} from "lucide-react";

const ServicesPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const serviceIcons = {
    "Core Service Lines": <Building className="w-8 h-8" />,
    "Technology Infrastructure": <Code className="w-8 h-8" />,
    "Target Market Segments": <Target className="w-8 h-8" />,
    "Competitive Advantages": <Star className="w-8 h-8" />,
    "Business Structure": <Users className="w-8 h-8" />,
    "Marketing & Client": <TrendingUp className="w-8 h-8" />,
    "Revenue Forecast": <BarChart3 className="w-8 h-8" />,
    "Legal & Compliance": <Shield className="w-8 h-8" />,
  };

  const services = {
    "Core Service Lines": {
      title: "Core Service Lines",
      description:
        "Katella Venture Capital LLC is a multi-service business firm committed to supporting entrepreneurs, investors, and small businesses with practical, growth-focused solutions.",
      servicesList: [
        "Capital Investment",
        "Real Estate Investment",
        "Zero Reporting Services",
        "Tax Accounting",
        "Construction Modelling",
        "Payroll Services",
      ],
      color: "from-blue-400 to-blue-600",
      bgColor: "from-blue-500/20 to-blue-600/20",
      link: "/services/core-service-lines-and-revenue-streams",
    },
    "Technology Infrastructure": {
      title: "Technology Infrastructure",
      description:
        "All Katella Venture Capital services are supported by customized enterprise technology tools built by Flynn Holding Digital LLC, including:",
      servicesList: [
        "Digital venture fund dashboard (for partners and LPs)",
        "Real estate analytics & portfolio tracker",
        "Automated tax and zero-reporting platform",
        "Construction modeling & rendering toolkits",
        "Secure client portals for payroll and accounting",
      ],
      color: "from-green-400 to-green-600",
      bgColor: "from-green-500/20 to-green-600/20",
      link: "/services/technology-infrastructure",
    },
    "Target Market Segments": {
      title: "Target Market Segments",
      description:
        "Our solutions are tailored for clients seeking expert support in venture funding, financial compliance, property investment, and business infrastructure, especially those in growth or transition phases.",
      servicesList: [
        "Real Estate Developers",
        "Startups & Founders",
        "SMEs & Corporates",
        "International Firms",
      ],
      color: "from-purple-400 to-purple-600",
      bgColor: "from-purple-500/20 to-purple-600/20",
      link: "/services/target-market-segments",
    },
    "Competitive Advantages": {
      title: "Competitive Advantages",
      description:
        "With flexible service options and a focus on long-term value, we offer clients a strategic edge in today's competitive business landscape.",
      servicesList: [
        "Portfolio Excellence",
        "Market Presence",
        "Texas-Based Operations",
        "Unlimited Scalability",
        "Full Compliance",
        "Results Driven",
      ],
      color: "from-orange-400 to-orange-600",
      bgColor: "from-orange-500/20 to-orange-600/20",
      link: "/services/competitive-advantages",
    },
    "Business Structure": {
      title: "Business Structure",
      description:
        "Katella Venture Capital LLC operates through a structured framework of specialized service divisions, each led by experienced professionals in their respective fields.",
      servicesList: [
        "Managing Partners",
        "Venture Analysts",
        "Tax Professionals & CPAs",
        "Real Estate & Construction Teams",
        "Payroll & HR Compliance Officers",
        "Technology Liaison Unit",
      ],
      color: "from-teal-400 to-teal-600",
      bgColor: "from-teal-500/20 to-teal-600/20",
      link: "/services/business-structure",
    },
    "Marketing & Client": {
      title: "Marketing & Client Acquisition",
      description:
        "By showcasing our expertise through thought leadership, personalized outreach, and strong client relationships, we attract and retain businesses seeking reliable, long-term advisory and support services.",
      servicesList: [
        "B2B digital campaigns via LinkedIn & Google",
        "Hosting webinars and pitch events",
        "Strategic partnerships networks",
        "VC and real estate summit participation",
        "Affiliate & referral programs",
      ],
      color: "from-pink-400 to-pink-600",
      bgColor: "from-pink-500/20 to-pink-600/20",
      link: "/services/marketing-client",
    },
    "Revenue Forecast": {
      title: "Revenue Forecast",
      description:
        "In the first two years, Katella Venture Capital LLC projects steady growth driven by diversified service lines. Revenue is expected to reach approximately $250,000 in Year 1 through advisory fees, tax services, and real estate consulting.",
      servicesList: [
        "VC Investment Returns",
        "Tax & Compliance Services",
        "Real Estate Project Management",
        "Construction Modeling & Rendering",
        "Zero Reporting Solutions",
        "Comprehensive Payroll Services",
      ],
      color: "from-indigo-400 to-indigo-600",
      bgColor: "from-indigo-500/20 to-indigo-600/20",
      link: "/services/revenue-forecast",
    },
    "Legal & Compliance": {
      title: "Legal & Compliance",
      description:
        "We ensure that our operations and client services adhere to federal, state, and industry-specific guidelines, including IRS regulations and corporate governance standards.",
      servicesList: [
        "Registered with the Texas Secretary of State",
        "SEC compliance and reporting",
        "Licensed CPAs and tax professionals",
        "Comprehensive partnership agreements",
        "Enterprise-grade data protection",
      ],
      color: "from-red-400 to-red-600",
      bgColor: "from-red-500/20 to-red-600/20",
      link: "/services/legal-compliance",
    },
  };

  const stats = [
    {
      number: "8",
      label: "Service Lines",
      icon: <Briefcase className="w-6 h-6" />,
    },
    {
      number: "$250K",
      label: "Year 1 Target",
      icon: <DollarSign className="w-6 h-6" />,
    },
    {
      number: "100%",
      label: "Compliance Rate",
      icon: <Shield className="w-6 h-6" />,
    },
    {
      number: "24/7",
      label: "Support Available",
      icon: <Globe className="w-6 h-6" />,
    },
  ];

  const serviceKeys = Object.keys(services);

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Header />
      <div className="min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Animated Background Elements */}

        {/* Hero Section */}
        <div className="container relative z-10 px-6 pt-20 pb-16 mx-auto">
          <Fade cascade triggerOnce>
            <div className="mb-16 text-center">
              <div className="inline-flex items-center px-6 py-3 mb-8 border rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-white/10">
                <Sparkles className="w-5 h-5 mr-2 text-yellow-400 animate-spin" />
                <span className="text-sm font-medium text-white/90">
                  Complete Business Solutions
                </span>
              </div>

              <h1 className="mb-6 text-6xl font-black leading-tight text-transparent md:text-8xl bg-clip-text bg-gradient-to-r from-white via-blue-200 to-purple-200">
                Our
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Services
                </span>
              </h1>

              <p className="max-w-3xl mx-auto mb-8 text-xl leading-relaxed text-white/70">
                From venture capital to technology infrastructure, we provide
                comprehensive solutions that scale with your ambitions and drive
                sustainable growth.
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  className="flex items-center px-8 py-4 font-semibold text-white transition-all duration-300 transform rounded-full group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 hover:shadow-2xl"
                >
                  <Play className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                  Explore Solutions
                </button>
                <button
                  type="button"
                  className="flex items-center px-8 py-4 font-semibold text-white transition-all duration-300 border rounded-full group bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20"
                >
                  Get Started Today
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Fade>
        </div>

        {/* Stats Section */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {stats.map((stat, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="p-6 text-center transition-all duration-300 border group bg-white/5 backdrop-blur-sm border-white/10 rounded-2xl hover:bg-white/10 hover:scale-105"
                >
                  <div className="flex justify-center mb-3 text-blue-400 group-hover:animate-bounce">
                    {stat.icon}
                  </div>
                  <div className="mb-1 text-3xl font-bold text-white">
                    {stat.number}
                  </div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </Fade>
        </div>

        {/* Services Grid */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold text-white md:text-5xl">
                Complete{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Service Portfolio
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-lg text-white/70">
                Eight comprehensive service lines designed to accelerate your
                business growth and ensure long-term success
              </p>
            </div>

            <div className="grid gap-8 mx-auto md:grid-cols-2 lg:grid-cols-2 max-w-7xl">
              {serviceKeys.map((serviceKey, index) => {
                const service = services[serviceKey];
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={`group relative bg-gradient-to-br ${
                      service.bgColor
                    } p-1 rounded-3xl transition-all duration-500 hover:scale-105 cursor-pointer ${
                      hoveredCard === index ? "scale-105" : ""
                    }`}
                    onMouseEnter={() => setHoveredCard(index)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <div className="h-full p-8 bg-slate-900/90 backdrop-blur-sm rounded-3xl">
                      <div className="flex items-center mb-6">
                        <div
                          className={`text-white mr-4 p-3 rounded-2xl bg-gradient-to-r ${service.color} group-hover:animate-pulse`}
                        >
                          {serviceIcons[serviceKey]}
                        </div>
                        <div>
                          <h3 className="mb-1 text-xl font-bold text-white">
                            {service.title}
                          </h3>
                          <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm">
                            <span className="text-xs font-medium text-white/80">
                              {service.servicesList.length} Features
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className="mb-6 leading-relaxed text-white/70 line-clamp-3">
                        {service.description}
                      </p>

                      <div className="mb-6 space-y-2 overflow-y-auto max-h-40">
                        {service.servicesList.map((item, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={idx} className="flex items-center">
                            <CheckCircle className="flex-shrink-0 w-4 h-4 mr-3 text-green-400" />
                            <span className="text-sm text-white/80">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>

                      <Link
                        to={service.link}
                        onClick={handleLinkClick}
                        className="flex items-center justify-center w-full py-3 font-medium text-white transition-all duration-300 border bg-white/10 hover:bg-white/20 backdrop-blur-sm border-white/20 rounded-xl group"
                      >
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </Fade>
        </div>

        {/* CTA Section */}
        <div className="container relative z-10 px-6 pb-20 mx-auto">
          <Fade triggerOnce>
            <div className="p-12 text-center border bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-white/10 rounded-3xl">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl">
                  <Sparkles className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>

              <h3 className="mb-4 text-3xl font-bold text-white md:text-4xl">
                Ready to Transform Your Business?
              </h3>

              <p className="max-w-2xl mx-auto mb-8 text-lg text-white/70">
                Choose from our comprehensive service portfolio or let us create
                a custom solution tailored to your unique business needs and
                growth objectives.
              </p>

              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  className="flex items-center justify-center px-10 py-4 font-semibold text-white transition-all duration-300 transform rounded-full group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 hover:shadow-2xl"
                >
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center px-10 py-4 font-semibold text-white transition-all duration-300 border rounded-full group bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20"
                >
                  Schedule Consultation
                </button>
              </div>
            </div>
          </Fade>
        </div>
      </div>
      <Discuss />
      <Footer />
    </>
  );
};

export default ServicesPage;
